import React from 'react';
import NowSelect from '../Common/NowSelect';
import PageTablePagination from './PageTablePagination';
import { EnumPagingMode, EnumSortOrder } from '../../constants/enum';
import { COMMON_CONST, PAGE_SIZE_OPTIONS } from '../../constants/constants';
import { isEmpty, isEmptyObject } from '../../utils/utils';
import { trans } from '../../localization';

export const PageTableHeader = ({ columns, onClick, sort, sortOrder }) => (
  <thead>
    <tr>
      {
        columns.map(column => {
          const {
            id,
            label,
            className,
            width,
            textAlign,
            sort: columnSort,
            ...rest
          } = column;

          return (
            <th
              className={
                `${
                  className || ''
                }text-align-${
                  textAlign || 'inherit'
                }${
                  !isEmpty(columnSort) ? ' clickable' : ''
                }${
                  // eslint-disable-next-line no-nested-ternary
                  (!isEmpty(sortOrder) && sort === columnSort)
                    ? (sortOrder === EnumSortOrder.ASC.id ? ' asc' : ' desc')
                    : ''
                }`
              }
              key={id}
              style={{
                fontWeight: 'bold',
                width,
                textAlign: textAlign || 'inherit',
                verticalAlign: 'middle',
                ...rest
              }}
              onClick={() => {
                if (onClick && !isEmpty(columnSort)) {
                  onClick(columnSort);
                }
              }}
            >
              {label} <span className="sort-order" />
            </th>
          )
        })
      }
    </tr>
  </thead>
);

export const PageTableRow = ({ className = '', children, ...rest }) => (
  <tr className={`${className} page-table-row`} {...rest}>
    {children}
  </tr>
);

export const PageTableCell = ({
  className = '',
  textAlign = 'inherit',
  verticalAlign,
  children,
  noBorder=false,
  noPadding=false,
  ...rest
}) => {
  const style = { ...(verticalAlign && { verticalAlign }), textAlign };
  if(noBorder){
    style.border = 0
  }
  if(noPadding){
    style.padding = 0
  }
  return (
    <td
      className={`${className} page-table-cell`}
      style={style}
      {...rest}
    >
      {children}
    </td>
  );
};

const PageTableFooter = props => {
  const {
    count,
    page,
    rowsPerPage,
    rowsPerPageOptions,
    onChangePage,
    onChangeRowsPerPage,
    // refreshList,
    paginationMode,
  } = props;
  const numberOfPage = parseInt(
    count / rowsPerPage + (count % rowsPerPage === 0 ? 0 : 1),
    10,
  );
  return (
      numberOfPage > 0 &&  <div className="footer-table">
        <div className="row justify-content-between align-items-center">
          <div className="col input-group align-items-center">
            {paginationMode !== EnumPagingMode.NONE.id  && (
            <React.Fragment>
              <PageTablePagination
                page={page}
                numberOfPage={numberOfPage}
                onChangePage={onChangePage}
              />
              <NowSelect
                width={100}
                className="page-size-select"
                isClearable={false}
                onChange={onChangeRowsPerPage}
                options={rowsPerPageOptions.map(option => ({
                  value: option,
                  label: option,
                }))}
                value={{ value: rowsPerPage, label: rowsPerPage }}
              />
            </React.Fragment>
          )}
          </div>
          <div className="col-auto">
            <div className="total-row">
              {`${(page - 1) * rowsPerPage + 1} - ${Math.min(
              page * rowsPerPage,
              count,
            )} ${trans('of')} ${count} ${trans('common_row')}`}
              {/* <button className="btn-load" onClick={refreshList}> */}
              {/* <i className="fas fa-undo" /> */}
              {/* </button> */}
            </div>
          </div>
        </div>
      </div>
  );
};

class PageTable extends React.Component {
  static defaultProps = {
    renderEmpty: () => null,
    renderExtraRowInfo: () => null,
    paginationMode: EnumPagingMode.NONE.id,
  };

  getItemListRender = () => {
    const { table, paginationMode } = this.props;
    const { itemList } = table;
    switch (paginationMode) {
      case EnumPagingMode.NONE.id: {
        return itemList;
      }
      case EnumPagingMode.SELF_PAGING.id: {
        const { activePage, pageSize } = table;
        return itemList.slice(
          (activePage - 1) * pageSize,
          activePage * pageSize,
        );
      }
      case EnumPagingMode.PAGING_IDS.id: {
        const { itemData = {}, activePage, pageSize } = table;
        return itemList
          .slice((activePage - 1) * pageSize, activePage * pageSize)
          .map(id => itemData[id])
          .filter(item => item);
      }
      default: {
        return itemList;
      }
    }
  };

  handleChangePage = page => {
    const { changeActivePage, getList, paginationMode } = this.props;
    if (paginationMode === EnumPagingMode.PAGING_IDS.id) {
      const { table } = this.props;
      const { itemData, itemList, pageSize } = table;
      const missingIds = itemList
        .slice((page - 1) * pageSize, page * pageSize)
        .filter(id => isEmptyObject(itemData[id]));
      getList(missingIds).then(() => changeActivePage(page));
    } else if (paginationMode === EnumPagingMode.PAGING_OFFSET_LIMIT.id) {
      changeActivePage(page);
      getList();
    } else {
      changeActivePage(page);
    }
  };

  handleChangeRowsPerPage = numberRowsPerPageOption => {
    const numberRowsPerPage = numberRowsPerPageOption.value;
    const {
      changePageSize,
      changeActivePage,
      getList,
      paginationMode,
    } = this.props;
    if (paginationMode === EnumPagingMode.PAGING_IDS.id) {
      const { table } = this.props;
      const { itemData, itemList } = table;
      const missingIds = itemList
        .slice(0, numberRowsPerPage)
        .filter(id => isEmptyObject(itemData[id]));
      getList(missingIds).then(() => {
        changeActivePage(1);
        changePageSize(numberRowsPerPage);
      });
    } else if (paginationMode === EnumPagingMode.PAGING_OFFSET_LIMIT.id) {
      changeActivePage(1);
      changePageSize(numberRowsPerPage);
      getList();
    } else {
      changeActivePage(1);
      changePageSize(numberRowsPerPage);
    }
  };

  render() {
    const {
      table,
      renderHeader,
      renderRow,
      renderEmpty,
      renderExtraRowInfo,
      getList,
      paginationMode,
      className,
      pageSizeOptions = PAGE_SIZE_OPTIONS,
      isShowFooter = true,
    } = this.props;
    const {
      itemList,
      activePage = 1,
      totalSize = itemList.length,
      pageSize = COMMON_CONST.PageSize,
    } = table;
    const mTotalSize =
      paginationMode !== EnumPagingMode.PAGING_OFFSET_LIMIT.id
        ? itemList.length
        : totalSize;
    return (
      <div
        className={`${className || ''}`}
        style={{ background: '#fff' }}
      >
        <div className="table-responsive">
          <table className="table table-striped projects" border="0">
            {renderHeader()}
            <tbody>
              {this.getItemListRender().map((row, idx) => {
                try {
                  return renderRow(row, (activePage - 1) * pageSize + idx + 1);
                } catch (ex) {
                  console.log('Render row fail:', row, ex);
                  return null;
                }
              })}
              {itemList.length === 0 && renderEmpty()}
              {renderExtraRowInfo()}
            </tbody>
          </table>
        </div>
        {isShowFooter && (
          <PageTableFooter
            count={mTotalSize}
            rowsPerPage={pageSize}
            page={activePage}
            rowsPerPageOptions={pageSizeOptions}
            onChangePage={this.handleChangePage}
            onChangeRowsPerPage={this.handleChangeRowsPerPage}
            refreshList={getList}
            paginationMode={paginationMode}
          />
        )}
      </div>
    );
  }
}

// How to use
// ALWAYS REQUIRE: paginationMode, renderHeader, renderRow
// + If paginationMode = NONE -> REQUIRE: table.itemList
// + If paginationMode = SELF_PAGING -> REQUIRE:
//   changePageSize, changeActivePage,
//   table.itemList, table.activePage, table.pageSize

// + If paginationMode = PAGING_OFFSET_LIMIT -> REQUIRE:
//   getList (get new itemList base on your activePage and pageSize),
//   changePageSize, changeActivePage,
//   table.itemList, table.totalSize, table.activePage, table.pageSize
// + If paginationMode = PAGING_IDS -> REQUIRE:
//   getList(missingIds) (get new item in table.itemData base on missing ids),
//   changePageSize, changeActivePage,
//   table.itemList (list ids), table.itemData (data map from id), table.activePage, table.pageSize

export default PageTable;



// WEBPACK FOOTER //
// ./foody/src/components/Table/PageTable.jsx